import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { OrderPickup, Store, CheckoutStep } from '@shared/schema';
import { createOrderPickup, getOrderPickupById, updateOrderPickup } from '../lib/orderService';
import { getDoorDashDeliveryByOrderId } from '../lib/doordashService';

// Utility to clean nulls and convert numbers to strings for text-based fields
function removeNullsAndNormalize(obj: any): any {
  const fieldsToStringify = [
    'orderTotal', 'serviceFee', 'deliveryFee', 'expressFee',
    'totalFee', 'tipAmount'
  ];

  const cleaned: any = {};
  for (const key in obj) {
    const value = obj[key];
    if (value === null) {
      cleaned[key] = undefined;
    } else if (fieldsToStringify.includes(key) && typeof value === 'number') {
      cleaned[key] = value.toString();
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

interface OrderPickupState {
  orderPickup: Partial<OrderPickup>;
  store: Store | null;
  step: CheckoutStep;
  loading: boolean;
  error: string | null;
  updateOrderPickupState: (data: Partial<OrderPickup>) => void;
  setStore: (store: Store) => void;
  setStep: (step: CheckoutStep) => void;
  reset: () => void;

  createOrder: (data: Partial<OrderPickup>) => Promise<OrderPickup>;
  fetchOrderById: (orderId: number) => Promise<void>;
  updateOrder: (orderId: number, data: Partial<OrderPickup>) => Promise<void>;
  fetchDeliveryStatus: (orderId: number) => Promise<any>;
}

export const useOrderPickupStore = create<OrderPickupState>()(
  persist(
    (set) => ({
      orderPickup: {},
      store: null,
      step: 'store_details',
      loading: false,
      error: null,

  updateOrderPickupState: (data) => set((state) => ({
    orderPickup: { ...state.orderPickup, ...data }
  })),

  setStore: (store) => set(() => ({ store })),
  setStep: (step) => set(() => ({ step })),

  reset: () => set(() => ({
    orderPickup: {},
    store: null,
    step: 'store_details',
    loading: false,
    error: null,
  })),

  createOrder: async (data) => {
    set({ loading: true, error: null });
    try {
      const sanitizedData = removeNullsAndNormalize(data);
      const createdOrder = (await createOrderPickup(sanitizedData)) as unknown as OrderPickup;
      const normalizedOrder = removeNullsAndNormalize(createdOrder) as OrderPickup;
      set({ orderPickup: normalizedOrder, loading: false });
      return normalizedOrder;
    } catch (error: any) {
      set({ error: error.message || 'Failed to create order', loading: false });
      throw error; // Re-throw so callers know it failed
    }
  },

  fetchOrderById: async (orderId) => {
    set({ loading: true, error: null });
    try {
      const order = await getOrderPickupById(orderId);
      set({ orderPickup: removeNullsAndNormalize(order), loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch order', loading: false });
    }
  },

  updateOrder: async (orderId, data) => {
    set({ loading: true, error: null });
    try {
      const updatedOrder = await updateOrderPickup(orderId, removeNullsAndNormalize(data));
      set({ orderPickup: removeNullsAndNormalize(updatedOrder), loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to update order', loading: false });
    }
  },

  fetchDeliveryStatus: async (orderId) => {
    set({ loading: true, error: null });
    try {
      const delivery = await getDoorDashDeliveryByOrderId(String(orderId));
      set({ loading: false });
      return delivery;
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch delivery status', loading: false });
      return null;
    }
  },
}),
    {
      name: 'order-pickup-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields (not loading/error state)
        orderPickup: state.orderPickup,
        store: state.store,
        step: state.step,
      }),
    }
  )
);
